import React from 'react'

/**
 * A React component for rendering optimised images with various features.
 *
 * This component provides advanced image rendering capabilities, including lazy loading,
 * responsive sizing, placeholder rendering, and image optimization.
 *
 * @param {object} props - The properties for the CdnImage component.
 * @param {string} props.url - The URL of the image to be displayed.
 * @param {string} [props.alt=''] - The alternative text for the image.
 * @param {string} [props.className=''] - Additional CSS classes to be applied to the image.
 * @param {object} [props.style={ maxWidth: '100%' }] - Custom styles to be applied to the image.
 * @param {number|number[]} [props.steps=200] - The number of steps for responsive image sizing.
 * @param {boolean} [props.autoFormat=true] - Whether to automatically format the image for optimal delivery.
 * @param {boolean} [props.renderPlaceholder=true] - Whether to render a low-quality image placeholder (LQIP).
 * @param {boolean} [props.lazyLoad=true] - Whether to enable lazy loading for the image.
 * @param {boolean} [props.preload=false] - Whether to preload the image (for future use, needs to go in the HEAD).
 * @param {number} [props.quality=80] - The quality of the image (0-100).
 * @param {string} [props.width=''] - The width of the image.
 * @param {string} [props.height=''] - The height of the image.
 * @param {string} [props.sizes=''] - The sizes attribute for responsive images (e.g., "(max-width: 735px) 100vw, 736px").
 * @returns {React.ReactElement} The rendered image element.
 */
const CdnImage = ({
  url = '',
  alt = '',
  className = '',
  style = { maxWidth: '100%' },
  steps = 200,
  autoFormat = true, // Keep?
  renderPlaceholder = true, // LQIP
  lazyLoad = true,
  preload = false, // For future use, needs to go in the HEAD
  quality = 80,
  width = '',
  height = '',
  sizes = '' // e.g. (max-width: 735px) 100vw, 736px
}: {
  url: string
  alt?: string
  className?: string
  style?: any
  steps?: number | number[]
  autoFormat?: boolean
  renderPlaceholder?: boolean
  lazyLoad?: boolean
  preload?: boolean
  quality?: number
  width?: string
  height?: string
  sizes?: string
}): React.ReactElement => {
  const basePath = `https://cdn.differentdog.com/images/unsafe/quality:${quality}/`
  const sourceUrl = `plain/${url}`

  const stepsLowerBound = 50
  const stepsUpperBound = 3000
  let srcSets = []
  if (typeof steps === 'number') {
    for (let step = stepsLowerBound; step <= stepsUpperBound; step += steps) {
      srcSets.push(step)
    }
    if (srcSets[srcSets.length - 1] !== stepsUpperBound) srcSets.push(stepsUpperBound)
  } else {
    srcSets = steps
  }

  const generateSrcSet = (format: string): string => {
    return srcSets.map(step => `${basePath}width:${step}/format:${format}/${sourceUrl} ${step}w`).join(', ')
  }

  // ! Temp, disable placeholder for now
  renderPlaceholder = false

  return (
    <picture>
      <source type="image/avif" srcSet={generateSrcSet('avif')} sizes={sizes} />
      <source type="image/webp" srcSet={generateSrcSet('webp')} sizes={sizes} />
      <source type="image/png" srcSet={generateSrcSet('png')} sizes={sizes} />
      <img
        sizes={sizes}
        src={`${basePath}format:png/width:${srcSets[srcSets.length - 1]}/${sourceUrl}`}
        alt={alt}
        loading={lazyLoad ? 'lazy' : undefined}
        decoding="async"
        width={width || undefined}
        height={height || undefined}
        className={className}
        style={{
          ...style,
          ...(renderPlaceholder && srcSets.length
            ? {
                backgroundSize: 'cover',
                // backgroundImage: `url(${basePath}${lqip}format:jpg/width:${srcSets[srcSets.length - 1]}/${sourceUrl})`
                backgroundImage: `url(https://cdn.differentdog.com/placeholder/${url})`
              }
            : {})
        }}
      />
    </picture>
  )
}

export default CdnImage
